'use client';

import { useProductSearch } from '@dealerportal/react-helpers';
import { isJSON } from '@dealerportal/utilities';
import { type ComponentProps, lazy, useMemo } from 'react';
import ProductCardSkeleton from '../../../../../ui-components/src/lib/skeletons/ProductCardSkeleton';
// Import the sibling directly rather than through the library barrel: a
// barrel import here makes this module and the barrel depend on each other,
// and React Fast Refresh reads every barrel export the moment the barrel
// evaluates, which throws for whichever member is still mid-evaluation.
import { ProductCard } from '../ProductCard/ProductCard';

const Gallery = lazy(
  () => import('../../../../../ui-components/src/lib/Gallery')
);

interface FeaturedProductsProps {
  arrowposition?: 'inside' | 'outside';
  query?: string;
  count?: string | number;
  feature?: string;
  upids?: string;
  skus?: string;
  productType?: string[];
  type?: string;
  types?: string;
  brand?: string;
  brands?: string;
  excludeUpid?: string;
  className?: string;
  gridclassname?: string;
  itemclassname?: string;
  sort?: string;
  display?: string;
  galleryProps?: ComponentProps<typeof Gallery>;
  disableOrderSample?: boolean;
  lines?: string;
}

export const FeaturedProducts = ({
  arrowposition,
  query = '',
  count = 4,
  feature,
  upids,
  skus,
  type = '',
  types = '',
  brand = '',
  brands = '',
  excludeUpid,
  className,
  gridclassname,
  itemclassname,
  sort,
  display = '',
  arrows,
  autoPlay,
  autoplayspeed,
  centermode,
  containerclass,
  customtransition,
  dotlistclass,
  infinite,
  items,
  itemclass,
  partialvisible,
  partialvisibilitygutter,
  renderdotsoutside,
  responsive,
  showdots,
  slidestoslide,
  sliderclass,
  transitionduration,
  removeArrowOnDeviceType,
  children,
  disableOrderSample,
  lines,
}: FeaturedProductsProps & ComponentProps<typeof Gallery>) => {
  // Build query string with memoization to prevent unnecessary re-renders
  const queryString = useMemo(() => {
    let searchQuery = query;

    if (feature)
      searchQuery = buildQueryString(searchQuery!, 'feature', feature);
    if (excludeUpid)
      searchQuery = buildQueryString(searchQuery!, 'excludeUpid', excludeUpid);
    if (upids) searchQuery = buildQueryString(searchQuery!, 'upids', upids);
    if (skus) searchQuery = buildQueryString(searchQuery!, 'skus', skus);
    if (type || types) {
      searchQuery = buildQueryString(searchQuery!, 'type', type || types);
    }
    if (brand || brands) {
      searchQuery = buildQueryString(searchQuery!, 'brand', brand || brands);
    }
    if (sort) searchQuery += `&sort=${sort}`;
    if (lines) {
      searchQuery = buildQueryString(searchQuery!, 'lines', lines);
    }
    if (upids || skus) {
      searchQuery += '&spreadProducts=true';
    }

    return searchQuery as string;
  }, [
    query,
    feature,
    excludeUpid,
    upids,
    skus,
    type,
    types,
    brand,
    brands,
    sort,
    lines,
  ]);

  // Traditional search
  const productSearchResults = useProductSearch(
    queryString,
    Number(count) || 5
  );

  const products = productSearchResults.loading
    ? new Array(Number(count || 4)).fill('')
    : productSearchResults?.products || [];

  const galleryConfig = {
    arrows: arrows || 'true',
    arrowposition,
    removeArrowOnDeviceType: removeArrowOnDeviceType || 'md',
    autoPlay: autoPlay || false,
    infinite: infinite || 'false',
    itemclass: itemclass || '!ui-px-2',
    partialvisible: partialvisible || 'true',
    responsive: isJSON(responsive)
      ? responsive
      : JSON.stringify({
          md: {
            breakpoint: { max: 767, min: 0 },
            items: 1,
            partialVisibilityGutter: Number(partialvisibilitygutter) || 40,
            slidesToSlide: 1,
          },
          lg: {
            breakpoint: { max: 1023, min: 768 },
            items: 1,
            partialVisibilityGutter: Number(partialvisibilitygutter) || 40,
            slidesToSlide: 1,
          },
          xl: {
            breakpoint: { max: 1279, min: 1024 },
            items: Number(items) || 4,
            partialVisibilityGutter: Number(partialvisibilitygutter) || 40,
            slidesToSlide: Number(slidestoslide) || 2,
          },
          '2xl': {
            breakpoint: { max: 99999, min: 1280 },
            items: Number(items) || 4,
            partialVisibilityGutter: Number(partialvisibilitygutter) || 40,
            slidesToSlide: Number(slidestoslide) || 3,
          },
        }),
  };

  return (
    <div className={`featured-products ${className || ''}`}>
      {display === 'carousel' ? (
        productSearchResults.loading ? (
          <div className="ui-max-w-full ui-overflow-hidden">
            <div className="ui-animate-pulse ui-min-w-[1280px] ui-grid ui-gap-4 ui-grid-cols-4">
              <ProductCardSkeleton />
              <ProductCardSkeleton />
              <ProductCardSkeleton />
              <ProductCardSkeleton />
            </div>
          </div>
        ) : (
          <Gallery
            {...{
              autoplayspeed,
              centermode,
              containerclass,
              customtransition,
              dotlistclass,
              renderdotsoutside,
              showdots,
              slidestoslide,
              sliderclass,
              transitionduration,
              children,
              ...galleryConfig,
            }}
          >
            {products.map((product: any, index: number) => (
              <div
                key={
                  product?.UPID || product?.sku || `featured-product-${index}`
                }
                data-cy="featured-product-item"
                className={`featured-products__item ui-h-full ui-border-solid ui-border ui-border-gray-300 ui-rounded-md ui-overflow-hidden ${
                  itemclassname || ''
                }`}
              >
                <ProductCard
                  product={product}
                  disableOrderSample={disableOrderSample}
                  showSwatches={product?.swatches?.length > 0}
                />
              </div>
            ))}
          </Gallery>
        )
      ) : (
        <div
          data-cy="featured-products-list"
          className={`featured-products__grid ui-grid sm:ui-grid-cols-2 lg:ui-grid-cols-4 ui-gap-4 ${
            gridclassname || ''
          }`}
        >
          {products?.map((product: any, index: number) => (
            <div
              key={
                product?.UPID ||
                product?.sku ||
                `featured-product-grid-${index}`
              }
              data-cy="featured-product-item"
              className={`featured-products__item ui-border-solid ui-border ui-border-gray-300 ui-rounded-md ui-overflow-hidden ${
                itemclassname || ''
              }`}
            >
              {productSearchResults.loading ? (
                <ProductCardSkeleton />
              ) : (
                <ProductCard
                  product={product}
                  disableOrderSample={disableOrderSample}
                  showSwatches={product?.swatches?.length > 0}
                />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const buildQueryString = (
  query: string,
  key: string,
  value: string | string[]
) => {
  if (value != null) {
    const processesedValue = Array.isArray(value) ? value?.join(',') : value;
    const queryParam = `${key}=${processesedValue}`;
    query = query ? `${query}&${queryParam}` : queryParam;
  }

  return query;
};

export default FeaturedProducts;
